import axios from "axios";
import { API_URLS } from "@/config/urls";
import { handleAxiosError, isAxiosError } from "@/lib/apiErrors";
import { api } from "@/lib/axios";
import type {
  Comment,
  CreateCommentResponse,
  PaginatedResponse,
  ReactionData,
  ReactionResponse,
  ReactionType,
} from "@/lib/humhub-api/types";

type TopicPayload = {
  id?: number | string;
  name: string;
  topic_kind?: string;
};

const appendTopicsToFormData = (
  formData: FormData,
  topics?: TopicPayload[],
) => {
  if (!topics?.length) return;
  topics.forEach((topic, index) => {
    formData.append(`data[content][topics][${index}][name]`, topic.name);
    if (topic.topic_kind) {
      formData.append(
        `data[content][topics][${index}][topic_kind]`,
        topic.topic_kind,
      );
    }
    if (topic.id !== undefined && topic.id !== null) {
      formData.append(`data[content][topics][${index}][id]`, String(topic.id));
    }
  });
};

export const commentsApi = {
  getComments: async (
    contentId: number | string,
    page = 1,
    perPage = 20,
  ): Promise<PaginatedResponse<Comment>> => {
    try {
      // Add timeout to prevent infinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout

      const response = await api.get<PaginatedResponse<Comment>>(
        `${API_URLS.COMMENT}/content/${contentId}`,
        {
          params: {
            page,
            limit: perPage,
          },
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);
      // Check if the API returns the expected structure
      if (!response.data.results && Array.isArray(response.data)) {
        // If API directly returns an array instead of the expected structure
        return {
          results: response.data,
          total: response.data.length,
          page: page,
          pages: 1,
          links: {
            self: "",
            first: "",
            last: "",
            next: undefined,
            prev: undefined,
          },
        };
      }

      return response.data;
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        console.error(`API call for post ${contentId} comments timed out`);
        throw new Error("Request timed out");
      }

      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred");
    }
  },

  createComment: async (
    objectId: number | string,
    message: string,
    files?: File[],
    topics?: TopicPayload[],
  ): Promise<CreateCommentResponse> => {
    try {
      // Validate objectId
      if (!objectId || objectId === "0" || objectId === 0) {
        console.error("Invalid objectId in createComment:", objectId);
        throw new Error("Invalid object ID");
      }

      const formData = new FormData();
      formData.append("objectModel", "humhub\\modules\\post\\models\\Post");
      formData.append("objectId", objectId.toString());
      formData.append("message", message);
      appendTopicsToFormData(formData, topics);

      // Add files if provided
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append("files[]", file);
          formData.append("fileList[]", file.name);
        });
      }

      // Use FormData for the request
      const response = await api.post("comment/with-files", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      console.error("Unexpected error in createComment:", error);
      throw new Error("Unexpected error occurred");
    }
  },

  createCommentReply: async (
    commentId: number | string,
    message: string,
    files?: File[],
    topics?: TopicPayload[],
  ): Promise<CreateCommentResponse> => {
    try {
      // Validate commentId
      if (!commentId || commentId === "0" || commentId === 0) {
        console.error("Invalid commentId in createCommentReply:", commentId);
        throw new Error("Invalid comment ID");
      }

      const formData = new FormData();
      formData.append(
        "objectModel",
        "humhub\\modules\\comment\\models\\Comment",
      );
      formData.append("objectId", commentId.toString());
      formData.append("message", message);
      appendTopicsToFormData(formData, topics);

      // Add files if provided
      if (files && files.length > 0) {
        files.forEach((file) => {
          formData.append("files[]", file);
          formData.append("fileList[]", file.name);
        });
      }

      // Use FormData for the request
      const response = await api.post("comment/with-files", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      console.error("Unexpected error in createCommentReply:", error);
      throw new Error("Unexpected error occurred");
    }
  },

  createOrDeleteReaction: async (
    commentId: number | string,
    reaction: ReactionType | null,
    objectType: "comment",
  ): Promise<ReactionResponse> => {
    try {
      const reactionData: ReactionData = {
        objectModel: objectType,
        objectId: commentId,
        Reaction: { emotion: reaction },
      };

      const response = await api.post(
        "reaction/create-or-delete",
        reactionData,
      );
      return {
        success: true,
        currentUserReaction: response.data.currentUserReaction || null,
      };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("Reaction API Error:", error.response?.data || error);
        throw error;
      }
      console.error("Reaction API Error:", error);
      throw new Error("Unexpected error occurred");
    }
  },

  deleteComment: async (commentId: number | string): Promise<void> => {
    try {
      await api.delete(`comment/${commentId}`);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(error, "Failed to delete comment");
      }
      console.error("[commentsApi] deleteComment unexpected error", {
        commentId,
        error,
      });
      throw new Error("Unexpected error occurred");
    }
  },

  editComment: async (
    commentId: number | string,
    newMessage: string,
  ): Promise<Comment> => {
    try {
      const response = await api.put<Comment>(`comment/${commentId}`, {
        message: newMessage,
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("API Error:", error.response.data);
        throw error;
      }
      console.error("Unexpected error in editComment:", error);
      throw new Error("Unexpected error occurred");
    }
  },

  removeCommentFile: async (
    commentId: number,
    fileId: number,
  ): Promise<void> => {
    await api.delete(`comment/${commentId}/remove-file/${fileId}`);
  },

  replaceCommentFile: async (
    commentId: number,
    fileId: number,
    newFile: File,
  ): Promise<void> => {
    const formData = new FormData();
    formData.append("files", newFile);
    await api.post(
      `comment/${commentId}/replace-file?fileId=${fileId}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
  },

  uploadFilesToComment: async (
    commentId: number,
    files: File[],
  ): Promise<void> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files[]", file);
    });
    await api.post(`comment/${commentId}/upload-files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};
